import { JobType } from '../../redux/jobs/jobs.types';
import { hasExportColumnValue } from './ColumnValueResolver';

export interface ColumnDef {
    key: string;
    labelKey: string;
    uiLabelKey: string;
    excelLabelKey: string;
}

export interface ColumnGroup {
    key: string;
    labelKey: string;
    columns: ColumnDef[];
}

// Frozen / always-present columns are not part of COLUMN_GROUPS (they are not
// user-selectable), but they still need a localized header. Their labelKeys live
// under the same i18n namespace so the backoffice can rename them via an
// overridden language file, exactly like the optional columns.
const FROZEN_LABEL_KEYS: Record<string, string> = {
    'meter.meterNumber': 'management.excelExport.columns.fields.meterNumber',
    'meterRateTask.obis': 'management.excelExport.columns.fields.obis',
    '__readingDate': 'management.excelExport.columns.fields.readingDate',
    '__readingValue': 'management.excelExport.columns.fields.readingValue',
    '__readingHint': 'management.excelExport.columns.fields.readingHint',
    '__status': 'management.excelExport.columns.fields.status',
    'meterRateTask.targetReadingDate': 'management.excelExport.columns.fields.targetReadingDate',
};

const legacyExcelLabelKeys: Record<string, string> = {
    'customer.postalAddress': 'management.excelExport.columns.excelFields.customerPostalAddress',
};

const column = (key: string, uiLabelKey: string, excelLabelKey: string): ColumnDef => ({
    key,
    labelKey: uiLabelKey,
    uiLabelKey,
    excelLabelKey,
});

export const COLUMN_GROUPS: ColumnGroup[] = [
    {
        key: 'customer',
        labelKey: 'management.excelExport.columns.groups.customer',
        columns: [
            column('customer.customerNumber', 'management.excelExport.columns.fields.customerNumber', 'management.excelExport.columns.excelFields.customerNumber'),
            column('customer.name', 'management.excelExport.columns.fields.customerName', 'management.excelExport.columns.excelFields.customerName'),
            column('customer.company', 'management.excelExport.columns.fields.customerCompany', 'management.excelExport.columns.excelFields.customerCompany'),
            column('customer.mailingAddress.street', 'management.excelExport.columns.fields.mailingAddressStreet', 'management.excelExport.columns.excelFields.customerMailingAddressStreet'),
            column('customer.mailingAddress.zip', 'management.excelExport.columns.fields.mailingAddressZip', 'management.excelExport.columns.excelFields.customerMailingAddressZip'),
            column('customer.mailingAddress.city', 'management.excelExport.columns.fields.mailingAddressCity', 'management.excelExport.columns.excelFields.customerMailingAddressCity'),
            column('customer.mailingAddress.postbox', 'management.excelExport.columns.fields.mailingAddressPostbox', 'management.excelExport.columns.excelFields.customerMailingAddressPostbox'),
            column('customer.contact.email', 'management.excelExport.columns.fields.email', 'management.excelExport.columns.excelFields.customerContactEmail'),
            column('customer.contact.phone', 'management.excelExport.columns.fields.phone', 'management.excelExport.columns.excelFields.customerContactPhone'),
            column('customer.contact.mobile', 'management.excelExport.columns.fields.mobile', 'management.excelExport.columns.excelFields.customerContactMobile'),
        ],
    },
    {
        key: 'partner',
        labelKey: 'management.excelExport.columns.groups.partner',
        columns: [
            column('partner.partnerKey', 'management.excelExport.columns.fields.partnerKey', 'management.excelExport.columns.excelFields.partnerKey'),
            column('partner.name', 'management.excelExport.columns.fields.partnerName', 'management.excelExport.columns.excelFields.partnerName'),
            column('partner.partnerCompany', 'management.excelExport.columns.fields.partnerCompany', 'management.excelExport.columns.excelFields.partnerCompany'),
            column('partner.contractId', 'management.excelExport.columns.fields.partnerContractId', 'management.excelExport.columns.excelFields.partnerContractId'),
            column('partner.mailingAddress.street', 'management.excelExport.columns.fields.mailingAddressStreet', 'management.excelExport.columns.excelFields.partnerMailingAddressStreet'),
            column('partner.mailingAddress.zip', 'management.excelExport.columns.fields.mailingAddressZip', 'management.excelExport.columns.excelFields.partnerMailingAddressZip'),
            column('partner.mailingAddress.city', 'management.excelExport.columns.fields.mailingAddressCity', 'management.excelExport.columns.excelFields.partnerMailingAddressCity'),
            column('partner.mailingAddress.postbox', 'management.excelExport.columns.fields.mailingAddressPostbox', 'management.excelExport.columns.excelFields.partnerMailingAddressPostbox'),
            column('partner.contact.email', 'management.excelExport.columns.fields.email', 'management.excelExport.columns.excelFields.partnerContactEmail'),
            column('partner.contact.phone', 'management.excelExport.columns.fields.phone', 'management.excelExport.columns.excelFields.partnerContactPhone'),
            column('partner.contact.mobile', 'management.excelExport.columns.fields.mobile', 'management.excelExport.columns.excelFields.partnerContactMobile'),
        ],
    },
    {
        key: 'location',
        labelKey: 'management.excelExport.columns.groups.location',
        columns: [
            column('meter.address.street', 'management.excelExport.columns.fields.meterAddressStreet', 'management.excelExport.columns.excelFields.meterAddressStreet'),
            column('meter.address.zip', 'management.excelExport.columns.fields.meterAddressZip', 'management.excelExport.columns.excelFields.meterAddressZip'),
            column('meter.address.city', 'management.excelExport.columns.fields.meterAddressCity', 'management.excelExport.columns.excelFields.meterAddressCity'),
            column('meter.address.objectKey', 'management.excelExport.columns.fields.meterAddressObjectKey', 'management.excelExport.columns.excelFields.meterAddressObjectKey'),
            column('meter.address.locationDescription', 'management.excelExport.columns.fields.meterAddressLocationDescription', 'management.excelExport.columns.excelFields.meterAddressLocationDescription'),
            column('meter.address.locationHint', 'management.excelExport.columns.fields.meterAddressLocationHint', 'management.excelExport.columns.excelFields.meterAddressLocationHint'),
        ],
    },
    {
        key: 'tariff',
        labelKey: 'management.excelExport.columns.groups.tariff',
        columns: [
            column('job.externalIdentifier', 'management.excelExport.columns.fields.externalOrderId', 'management.excelExport.columns.excelFields.externalOrderId'),
            column('meterRateTask.maloId', 'management.excelExport.columns.fields.maLoId', 'management.excelExport.columns.excelFields.maLoId'),
            column('meterRateTask.meloId', 'management.excelExport.columns.fields.meloId', 'management.excelExport.columns.excelFields.meloId'),
            column('meterRateTask.meterType', 'management.excelExport.columns.fields.meterType', 'management.excelExport.columns.excelFields.meterType'),
            column('meterRateTask.tariffName', 'management.excelExport.columns.fields.tariffName', 'management.excelExport.columns.excelFields.tariffName'),
            column('meterRateTask.unit', 'management.excelExport.columns.fields.unit', 'management.excelExport.columns.excelFields.unit'),
            column('meterRateTask.executionDeadline', 'management.excelExport.columns.fields.executionDeadline', 'management.excelExport.columns.excelFields.executionDeadline'),
            column('meterRateTask.validUntil', 'management.excelExport.columns.fields.validUntil', 'management.excelExport.columns.excelFields.validUntil'),
        ],
    },
    {
        key: 'history',
        labelKey: 'management.excelExport.columns.groups.history',
        columns: [
            column('meterRateTask.lastReadingDate', 'management.excelExport.columns.fields.lastReadingDate', 'management.excelExport.columns.excelFields.lastReadingDate'),
            column('meterRateTask.lastReadingValue', 'management.excelExport.columns.fields.lastReadingValue', 'management.excelExport.columns.excelFields.lastReadingValue'),
            column('meterRateTask.lastResultDate', 'management.excelExport.columns.fields.lastResultDate', 'management.excelExport.columns.excelFields.lastResultDate'),
            column('meterRateTask.lastResultValue', 'management.excelExport.columns.fields.lastResultValue', 'management.excelExport.columns.excelFields.lastResultValue'),
        ],
    },
];

// Flat map: column key -> i18n labelKey, covering both the optional grouped
// columns and the fixed/frozen columns. Used to resolve localized Excel headers.
// Seit dem Gruppenband-Layout nutzen die Excel-Header die kurzen UI-Labels
// (z.B. "PLZ" statt "Zählpunkt: PLZ") — die Gruppenzugehörigkeit steht bereits
// im Band über der Header-Zeile.
export const COLUMN_LABEL_KEYS: Record<string, string> = {
    ...FROZEN_LABEL_KEYS,
    ...legacyExcelLabelKeys,
    ...Object.fromEntries(COLUMN_GROUPS.flatMap((g) => g.columns.map((c) => [c.key, c.uiLabelKey]))),
};

// ─── Gruppenband im Excel-Export ─────────────────────────────────────────────
// Über der Header-Zeile zeigt der Export ein Band, das zusammengehörige Spalten
// gruppiert (Zähler / Ablesung / Standort / Kunde / Auftrag / Historie).
// 'meter' und 'reading' sind reine Band-Gruppen für die festen Spalten; die
// übrigen Bänder entsprechen den wählbaren COLUMN_GROUPS.

export const BAND_GROUP_LABEL_KEYS: Record<string, string> = {
    meter: 'management.excelExport.columns.groups.meter',
    reading: 'management.excelExport.columns.groups.reading',
    location: 'management.excelExport.columns.groups.location',
    customer: 'management.excelExport.columns.groups.customer',
    partner: 'management.excelExport.columns.groups.partner',
    tariff: 'management.excelExport.columns.groups.tariff',
    history: 'management.excelExport.columns.groups.history',
};

// Dezente Füllfarben der Band-Gruppen (Band- und Header-Zeile).
export const BAND_GROUP_FILLS: Record<string, string> = {
    meter: 'FFF7DFC9',    // helles Orange
    reading: 'FFFFF2CC',  // helles Gelb (= Eingabebereich)
    location: 'FFE3EEDD', // helles Grün
    customer: 'FFD9E2F3', // helles Blau
    partner: 'FFD9E2F3',  // helles Blau (wie Kunde)
    tariff: 'FFE6E2EF',   // helles Violett
    history: 'FFE9EBEE',  // helles Grau
};

// Spalten-Key → Band-Gruppe. Wählbare Spalten aus COLUMN_GROUPS abgeleitet;
// die expliziten Einträge danach überschreiben gezielt: die festen/frozen
// Spalten sowie der Tarif, der im Schema zur Zähler-Identität vorgezogen wird.
export const COLUMN_BAND_GROUPS: Record<string, string> = {
    ...Object.fromEntries(COLUMN_GROUPS.flatMap((g) => g.columns.map((c) => [c.key, g.key]))),
    'meter.meterNumber': 'meter',
    'meterRateTask.obis': 'meter',
    'meterRateTask.tariffName': 'meter',
    '__readingDate': 'reading',
    '__readingValue': 'reading',
    '__readingHint': 'reading',
    '__status': 'reading',
    'meterRateTask.targetReadingDate': 'tariff',
    'customer.postalAddress': 'customer',
};

export function filterColumnGroupsByAvailableValues(jobs: JobType[]): ColumnGroup[] {
    return COLUMN_GROUPS
        .map(group => ({
            ...group,
            columns: group.columns.filter(column => hasExportColumnValue(column.key, jobs)),
        }))
        .filter(group => group.columns.length > 0);
}
